import { BarChart } from '@/components/charts/BarChart'
import { calculateItemMetrics } from '@/utils/metrics'
import type { ReportData } from '@/entities/report'

interface ReportsChartsProps {
  report: ReportData
}

export function ReportsCharts({ report }: ReportsChartsProps) {
  const data = report.data || []
  const grouping = report.grouping || ''
  const listNames = report.listNames

  // Get categories (group keys) and format them with list names if available
  const categories = data.map((item) => {
    const groupKey = item.groupKey.toString()
    if (grouping === 'list' && listNames?.[groupKey]) {
      const listName = listNames[groupKey]
      return listName.length > 15 ? listName.substring(0, 15) + '...' : listName
    }
    return groupKey
  })

  // Get full names for tooltips
  const fullNames = data.map((item) => {
    const groupKey = item.groupKey.toString()
    if (grouping === 'list' && listNames?.[groupKey]) {
      return listNames[groupKey]
    }
    return groupKey
  })

  // Calculate metrics for each group
  const metricsData = data.map((item) => calculateItemMetrics(item, { raw: true }) as Record<string, number>)

  // Extract data for each chart
  const totalCalls = metricsData.map((item) => item['Total calls'] || 0)
  const connectivityRates = metricsData.map((item) => item['% Connected calls'] || 0)
  const successRates = metricsData.map((item) => item['% Successful calls'] || 0)
  const totalCosts = metricsData.map((item) => item['Cost ($)'] || 0)
  const costPerSuccess = metricsData.map((item) => item['Cost per success'] || 0)

  // For calls over 60s, calculate from duration metrics
  const callsOver60s = data.map((item) => {
    const durationMetrics = item.durationMetrics || {}
    const over60sPercent = durationMetrics['% 60s+'] || 0
    return over60sPercent
  })

  if (data.length === 0) {
    return null
  }

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
      {/* Volume - Light Blue */}
      <BarChart title="Total Calls" data={totalCalls} categories={categories} color="#7dd3fc" fullNames={fullNames} />

      {/* Performance - Light Green */}
      <BarChart
        title="Connectivity (%)"
        data={connectivityRates}
        categories={categories}
        color="#86efac"
        formatter={(value) => `${value.toFixed(2)}%`}
        xAxisMin={0}
        xAxisMax={Math.max(...connectivityRates) > 80 ? 100 : undefined}
        fullNames={fullNames}
      />
      <BarChart
        title="Success Rate (%)"
        data={successRates}
        categories={categories}
        color="#a7f3d0"
        formatter={(value) => `${value.toFixed(2)}%`}
        xAxisMin={0}
        fullNames={fullNames}
      />

      {/* Cost - Light Orange/Pink */}
      <BarChart
        title="Total Cost ($)"
        data={totalCosts}
        categories={categories}
        color="#fca5a5"
        formatter={(value) => `$${value.toFixed(2)}`}
        xAxisMin={0}
        fullNames={fullNames}
      />
      <BarChart
        title="Cost per Success ($)"
        data={costPerSuccess}
        categories={categories}
        color="#fed7aa"
        formatter={(value) => `$${value.toFixed(2)}`}
        xAxisMin={0}
        fullNames={fullNames}
      />

      {/* Duration - Light Teal */}
      <BarChart
        title="Calls over 60s (%)"
        data={callsOver60s}
        categories={categories}
        color="#99f6e4"
        formatter={(value) => `${value.toFixed(2)}%`}
        xAxisMin={0}
        xAxisMax={Math.max(...callsOver60s) > 80 ? 100 : undefined}
        fullNames={fullNames}
      />
    </div>
  )
}